import React, { useMemo, useState } from 'react';
import { ActionSheet, Button, DotLoading, Toast } from 'antd-mobile';
import type { Action } from 'antd-mobile/es/components/action-sheet';
import { CheckCircleFill } from 'antd-mobile-icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LOCALE, LOCALE_LABELS, SUPPORTED_LOCALES, type SupportedLocale } from '../../../i18n';
import { profilePageStyles } from './styles';
import { ProfileState } from './types';

interface ProfileViewProps {
  state: ProfileState;
  onLogout: () => Promise<void>;
}

export const ProfileView: React.FC<ProfileViewProps> = ({ state, onLogout }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['profile', 'common']);
  const [languageVisible, setLanguageVisible] = useState(false);

  if (state.loading && !state.user) {
    return (
      <div className="webview-page" style={{ ...profilePageStyles.page, ...profilePageStyles.loadingPage }}>
        <DotLoading />
      </div>
    );
  }

  const user = state.user;
  const roleLabelMap: Record<string, string> = {
    promoter: t('rolePromoter'),
    operator: t('roleOperator'),
    super_admin: t('roleSuperAdmin'),
  };
  const roleLabel = user?.role ? roleLabelMap[user.role] || user.role : '-';
  const currentLocale = ((i18n.language as SupportedLocale) || DEFAULT_LOCALE);
  const currentLocaleLabel = LOCALE_LABELS[currentLocale];
  const isBound = Boolean(state.whatsappSummary?.boundCount);
  const bindStatusLabel = isBound ? t('bound') : t('unbound');
  const onlineSummaryLabel = state.whatsappSummary && state.whatsappSummary.onlineCount > 0
    ? t('onlineCountValue', { count: state.whatsappSummary.onlineCount })
    : (isBound ? t('bound') : t('unbound'));
  const languageActions: Action[] = useMemo(() => SUPPORTED_LOCALES.map((locale) => ({
    key: locale,
    text: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {locale === currentLocale ? <CheckCircleFill color="#6f73f6" fontSize={16} /> : <span style={{ width: 16, height: 16 }} />}
        <span>{LOCALE_LABELS[locale]}</span>
      </span>
    ),
    bold: locale === currentLocale,
    onClick: () => {
      void i18n.changeLanguage(locale);
      setLanguageVisible(false);
    },
  })), [currentLocale, i18n]);

  const openPlaceholder = () => {
    Toast.show({ content: t('comingSoon') });
  };

  return (
    <div className="webview-page" style={profilePageStyles.page}>
      <div style={profilePageStyles.topBar}>
        <div style={profilePageStyles.topBarLeft}>
          <div style={profilePageStyles.topBarIconWrap}>
            <ShieldTitleGlyph />
          </div>
          <div style={profilePageStyles.topBarTitle}>{t('title')}</div>
        </div>
        <button
          type="button"
          style={profilePageStyles.settingsButton}
          onClick={() => navigate('/profile/whatsapp-accounts')}
          aria-label={t('waAccountsAction')}
        >
          <SettingsGlyph />
        </button>
      </div>
      <div style={profilePageStyles.topDivider} />

      <div className="webview-section">
        <div style={profilePageStyles.heroCard}>
          <div style={profilePageStyles.heroGlowPrimary} />
          <div style={profilePageStyles.heroGlowSecondary} />
          <div style={profilePageStyles.heroTop}>
            <div style={profilePageStyles.heroUserRow}>
              <div style={profilePageStyles.avatarWrap}>
                <div style={profilePageStyles.avatar}>
                  <AvatarCardGlyph />
                </div>
                <span style={profilePageStyles.avatarStatusDot} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={profilePageStyles.heroName}>
                  {user?.display_name || '-'}
                  <span style={profilePageStyles.heroRoleBadge}>{roleLabel}</span>
                </div>
                <div style={profilePageStyles.heroUsername}>@{user?.username || '-'}</div>
              </div>
            </div>
          </div>
          <div style={profilePageStyles.heroDescription}>{t('heroDescription')}</div>
          <div style={profilePageStyles.heroMetaRow}>
            <div style={profilePageStyles.heroMetaText}>{t('accountStatusNormal')}</div>
            <div style={profilePageStyles.heroMetaText}>{t('bindStatus', { status: bindStatusLabel })}</div>
          </div>
        </div>
      </div>

      <div className="webview-section">
        <div style={profilePageStyles.summaryRow}>
          <div style={profilePageStyles.summaryCard}>
            <div style={{ ...profilePageStyles.summaryIconWrap, background: 'rgba(111, 115, 246, 0.10)', color: '#7b80f5' }}>
              <UserRoleGlyph />
            </div>
            <div style={profilePageStyles.summaryLabel}>{t('role')}</div>
            <div style={profilePageStyles.summaryValue}>{roleLabel}</div>
            <div style={profilePageStyles.summaryHint}>{t('rolePermissionDescription')}</div>
          </div>
          <button
            type="button"
            style={{ ...profilePageStyles.summaryCard, cursor: 'pointer' }}
            onClick={() => navigate('/profile/rebind')}
          >
            <div style={{ ...profilePageStyles.summaryIconWrap, background: 'rgba(83, 227, 202, 0.14)', color: '#4fd0bc' }}>
              <PhoneCardGlyph />
            </div>
            <div style={profilePageStyles.summaryLabel}>{t('contactPhone')}</div>
            <div style={profilePageStyles.summaryValue}>{user?.contact_phone ? t('configured') : t('notConfigured')}</div>
            <div style={profilePageStyles.summaryHint}>{t('phoneLoginPair')}</div>
          </button>
          <div style={profilePageStyles.summaryCard}>
            <div style={{ ...profilePageStyles.summaryIconWrap, background: 'rgba(154, 124, 255, 0.12)', color: '#8a75f1' }}>
              <WhatsAppCardGlyph />
            </div>
            <div style={profilePageStyles.summaryLabel}>WHATSAPP</div>
            <div style={profilePageStyles.summaryValue}>{onlineSummaryLabel}</div>
            <div style={profilePageStyles.summaryHint}>{t('whatsappCurrentStatus')}</div>
          </div>
        </div>
      </div>

      {user?.role === 'promoter' && (
        <div className="webview-section">
          <button
            type="button"
            style={profilePageStyles.pointsCard}
            onClick={() => navigate('/profile/points')}
          >
            <div style={profilePageStyles.pointsCardLeft}>
              <div style={profilePageStyles.pointsIconWrap}>
                <PointsGlyph />
              </div>
              <div>
                <div style={profilePageStyles.pointsLabel}>{t('totalPoints')}</div>
                <div style={profilePageStyles.pointsValue}>{state.totalPoints.toLocaleString()}</div>
              </div>
            </div>
            <div style={profilePageStyles.pointsAction}>{t('pointsDetailAction')}</div>
          </button>
        </div>
      )}

      <div style={profilePageStyles.sectionWrap}>
        <div style={profilePageStyles.sectionHeader}>
          <div>
            <div style={profilePageStyles.sectionTitle}>{t('quickEntry')}</div>
            <div style={profilePageStyles.sectionSubtitle}>{t('quickEntrySubtitle')}</div>
          </div>
        </div>
        <div style={profilePageStyles.quickList}>
          <button type="button" style={profilePageStyles.quickRow} onClick={() => navigate('/referral')}>
            <div style={{ ...profilePageStyles.quickIconWrap, background: 'rgba(255, 115, 160, 0.12)', color: '#f46d9b' }}>
              <ReferralGlyph />
            </div>
            <div style={profilePageStyles.quickTextWrap}>
              <div style={profilePageStyles.quickTitle}>{t('referralTitle')}</div>
              <div style={profilePageStyles.quickDescription}>{t('referralDescription')}</div>
            </div>
            <div style={profilePageStyles.arrow}>›</div>
          </button>

          <button type="button" style={profilePageStyles.quickRow} onClick={() => setLanguageVisible(true)}>
            <div style={{ ...profilePageStyles.quickIconWrap, background: 'rgba(83, 227, 202, 0.12)', color: '#4fd0bc' }}>
              <LanguageGlyph />
            </div>
            <div style={profilePageStyles.quickTextWrap}>
              <div style={profilePageStyles.quickTitle}>{t('languageEntryTitle')}</div>
            </div>
            <div style={profilePageStyles.quickValue}>{currentLocaleLabel}</div>
          </button>

          <button type="button" style={profilePageStyles.quickRow} onClick={openPlaceholder}>
            <div style={{ ...profilePageStyles.quickIconWrap, background: 'rgba(145, 153, 168, 0.12)', color: '#7d8698' }}>
              <PrivacyGlyph />
            </div>
            <div style={profilePageStyles.quickTextWrap}>
              <div style={profilePageStyles.quickTitle}>{t('privacyTitle')}</div>
            </div>
            <div style={profilePageStyles.arrow}>›</div>
          </button>
        </div>
      </div>

      <div style={profilePageStyles.sectionWrap}>
        <Button block style={profilePageStyles.logoutButton} onClick={() => void onLogout()}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <LogoutGlyph />
            <span>{t('logout')}</span>
          </span>
        </Button>
        <div style={profilePageStyles.versionText}>{t('versionText')}</div>
      </div>

      <ActionSheet
        visible={languageVisible}
        actions={languageActions}
        cancelText={t('common:cancel')}
        onClose={() => setLanguageVisible(false)}
      />
    </div>
  );
};

const ShieldTitleGlyph: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 2.5 15.417 4.417v4.583c0 3.333-2.083 6.083-5.417 7.5C6.666 15.083 4.583 12.333 4.583 9V4.417L10 2.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
  </svg>
);

const SettingsGlyph: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.077 3.423a1 1 0 0 1 1.846 0l.337.815a1 1 0 0 0 .949.616h.863a1 1 0 0 1 .707 1.707l-.61.61a1 1 0 0 0-.23 1.09l.337.814a1 1 0 0 1-1.299 1.299l-.814-.337a1 1 0 0 0-1.09.23l-.61.61a1 1 0 0 1-1.707-.707v-.863a1 1 0 0 0-.616-.949l-.815-.337a1 1 0 0 1 0-1.846l.815-.337a1 1 0 0 0 .616-.949V5.07a1 1 0 0 1 1.707-.707l.61.61a1 1 0 0 0 1.09.23l.814-.337a1 1 0 0 1 1.299 1.299l-.337.814a1 1 0 0 0 .23 1.09l.61.61a1 1 0 0 1-.707 1.707h-.863a1 1 0 0 0-.949.616l-.337.815a1 1 0 0 1-1.846 0l-.337-.815a1 1 0 0 0-.949-.616h-.863a1 1 0 0 1-.707-1.707l.61-.61a1 1 0 0 0 .23-1.09l-.337-.814a1 1 0 0 1 1.299-1.299l.814.337a1 1 0 0 0 1.09-.23l.61-.61A1 1 0 0 1 8.077 3.423Z" stroke="currentColor" strokeWidth="1.2"/>
    <circle cx="10" cy="10" r="2.2" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
);

const UserRoleGlyph: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="6.5" r="2.5" stroke="currentColor" strokeWidth="1.6"/>
    <path d="M5.5 15c.7-2.1 2.4-3.2 4.5-3.2s3.8 1.1 4.5 3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);

const PointsGlyph: React.FC = () => (
  <svg width="19" height="19" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 2.5 12.09 6.73l4.66.68-3.38 3.29.8 4.64L10 13.15 5.83 15.34l.8-4.64-3.38-3.29 4.66-.68L10 2.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
  </svg>
);

const PhoneCardGlyph: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="3.5" width="8" height="13" rx="2" stroke="currentColor" strokeWidth="1.6"/>
    <path d="M8.5 6h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    <circle cx="10" cy="14" r="0.8" fill="currentColor"/>
  </svg>
);

const WhatsAppCardGlyph: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.2 4.8A7.2 7.2 0 0 0 4.8 15.2L4 18l2.96-.78A7.2 7.2 0 1 0 15.2 4.8Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M7.8 7.6c.2-.4.42-.4.58-.4.15 0 .33 0 .5.01.16.01.38-.06.58.42.2.48.68 1.66.74 1.78.06.12.1.26.02.42-.08.16-.12.26-.24.4-.12.14-.25.3-.35.4-.12.12-.25.25-.1.5.16.24.7 1.15 1.5 1.86 1.04.93 1.92 1.22 2.18 1.36.26.14.42.12.58-.07.16-.2.66-.76.84-1.02.18-.26.36-.22.6-.13.25.1 1.56.73 1.82.86.26.13.44.2.5.31.06.12.06.68-.16 1.34-.22.66-1.28 1.26-1.76 1.34-.46.08-1.04.12-1.68-.08-.4-.13-.92-.3-1.58-.58-2.8-1.2-4.62-4.06-4.76-4.26-.14-.2-1.14-1.52-1.14-2.9 0-1.38.72-2.06.98-2.34Z" fill="currentColor"/>
  </svg>
);

const ReferralGlyph: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.4 4.2h7.2v2.2a3.6 3.6 0 0 1-2.52 3.44L10 10.2l-1.08-.36A3.6 3.6 0 0 1 6.4 6.4V4.2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M13.6 5.3h1.92a1 1 0 0 1 1 1v.18a3.68 3.68 0 0 1-3.68 3.68h-.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M6.4 5.3H4.48a1 1 0 0 0-1 1v.18a3.68 3.68 0 0 0 3.68 3.68h.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M10 10.2v2.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M7.2 15.2h5.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const LanguageGlyph: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M3.8 10h12.4M10 3.8c1.53 1.67 2.4 3.83 2.4 6.2S11.53 14.53 10 16.2M10 3.8C8.47 5.47 7.6 7.63 7.6 10s.87 4.53 2.4 6.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const PrivacyGlyph: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 2.8 14.8 4.5v4.06c0 2.95-1.84 5.4-4.8 6.64-2.96-1.24-4.8-3.7-4.8-6.64V4.5L10 2.8Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M8.4 9.7 9.5 10.8l2.2-2.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const LogoutGlyph: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.667 5.833V4.5A1.667 1.667 0 0 0 10 2.833H5A1.667 1.667 0 0 0 3.333 4.5v11A1.667 1.667 0 0 0 5 17.167h5A1.667 1.667 0 0 0 11.667 15.5v-1.333" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8.333 10h8.334m0 0-2.5-2.5m2.5 2.5-2.5 2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const AvatarCardGlyph: React.FC = () => (
  <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="3.5" y="5" width="49" height="37" rx="18.5" fill="#EEF1FF" stroke="rgba(255,255,255,0.85)" />
    <rect x="8.5" y="10" width="39" height="27" rx="11" fill="#FFFFFF" stroke="#D8E0FF"/>
    <rect x="11.5" y="13" width="33" height="18" rx="8" fill="url(#paint0_linear_avatar)" />
    <rect x="14.5" y="16" width="12" height="2.6" rx="1.3" fill="#9EA8FF" fillOpacity="0.9"/>
    <rect x="14.5" y="20.6" width="15" height="2.6" rx="1.3" fill="#C4CBFF" />
    <rect x="14.5" y="25.2" width="9" height="2.6" rx="1.3" fill="#D8DEFF" />
    <circle cx="37" cy="23" r="6.5" fill="#F8FBFF" fillOpacity="0.95"/>
    <path d="M34.4 22.8c.24-.46.49-.47.67-.47.17 0 .35 0 .53.01.17.01.42-.05.64.49.22.55.72 1.88.79 2.02.06.13.1.29.02.46-.09.18-.13.29-.28.45-.14.16-.28.34-.4.46-.13.14-.28.29-.11.56.17.27.77 1.32 1.66 2.13 1.14 1.02 2.11 1.34 2.4 1.49.28.15.46.13.63-.08.18-.22.73-.87.93-1.17.19-.3.39-.25.67-.15.27.11 1.72.83 2.01.97.29.14.48.22.56.35.07.14.07.77-.17 1.5-.24.73-1.41 1.4-1.95 1.49-.5.09-1.15.14-1.85-.09-.45-.15-1.01-.34-1.74-.65-3.08-1.33-5.08-4.47-5.23-4.69-.16-.22-1.25-1.67-1.25-3.2 0-1.52.79-2.28 1.08-2.59Z" fill="#6D74F3" transform="translate(-2.6 -2.2) scale(0.34)"/>
    <rect x="13" y="35.5" width="30" height="4" rx="2" fill="#E4E8FF"/>
    <defs>
      <linearGradient id="paint0_linear_avatar" x1="11.5" y1="13" x2="44.5" y2="31" gradientUnits="userSpaceOnUse">
        <stop stopColor="#D7DEFF"/>
        <stop offset="1" stopColor="#F7F9FF"/>
      </linearGradient>
    </defs>
  </svg>
);
